import { useCallback, useEffect, useState } from 'preact/hooks';
import type { Sct, SignedEntry } from '../../core/rfc6962/types';
import { checkInclusion, summarize, type InclusionCheck, type InclusionSummary } from '../inclusionCheck';
import { usePageState } from '../state/pageState';

export type CheckState = InclusionCheck | 'running' | undefined;

/**
 * Runs checkInclusion() for every SCT (all at once) and keeps the results in page state, so leaving
 * the page and coming back does not re-query the logs. `auto` starts the run as soon as SCTs exist.
 */
export function useInclusionChecks(key: string, scts: Sct[], entries: Array<SignedEntry | undefined>, auto = false): { checks: CheckState[]; summary: InclusionSummary; running: boolean; run: (force?: boolean) => void } {
  const [checks, setChecks] = usePageState<CheckState[]>(key, []);
  const [running, setRunning] = useState(false);
  const run = useCallback(
    (force = false) => {
      if (scts.length === 0) return;
      setRunning(true);
      setChecks(scts.map(() => 'running' as const));
      // One log at a time: each check is a couple of requests, and running them sequentially keeps
      // the per-log request queues (and the logs' rate limits) calm.
      void (async () => {
        for (const [i, sct] of scts.entries()) {
          const c = await checkInclusion(sct, entries[i], force);
          setChecks((prev) => { const next = prev.slice(); next[i] = c; return next; });
        }
      })().finally(() => setRunning(false));
    },
    [scts, entries],
  );
  useEffect(() => {
    if (auto && scts.length > 0 && checks.length !== scts.length) run();
  }, [auto, scts, entries]);
  return { checks, summary: summarize(checks.length === scts.length ? checks : scts.map(() => undefined)), running, run };
}
